import { useNavigate } from '@tanstack/react-router';

export interface OnboardingContext {
  isDesktop: boolean;
  selectedNovelId: string | null | undefined;
  hasChapters: boolean;
  firstChapterId: string | null | undefined;
  hasTextId?: boolean;
  characterCount: number;
  locationCount: number;
  sceneCount: number;
  firstSceneTextId?: string | null | undefined;
}

export interface TourStepConfig {
  target: string | ((ctx: OnboardingContext) => string);
  contentKey: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto' | 'center';
  path: string;
  disableBeacon?: boolean;
  isConditional?: (ctx: OnboardingContext) => boolean;
  waitForCondition?: (ctx: OnboardingContext) => boolean;
  onNext?: (navigate: ReturnType<typeof useNavigate>, ctx: OnboardingContext) => void | Promise<void>;
  onBefore?: (navigate: ReturnType<typeof useNavigate>, ctx: OnboardingContext) => void | Promise<void>;
}

function ensureDrawerState(targetButtonId: string, shouldBeOpen: boolean) {
  const btn = document.querySelector(targetButtonId);
  if (btn) {
    const isOpen = btn.classList.contains('MuiIconButton-colorPrimary');
    if (isOpen !== shouldBeOpen) {
      (btn as HTMLElement).click();
    }
  }
}

export function getNextActiveStepIndex(
  startIndex: number,
  direction: 'forward' | 'backward',
  ctx: OnboardingContext
): number {
  let i = startIndex;
  while (i >= 0 && i < tourSteps.length) {
    const step = tourSteps[i];
    if (!step.isConditional || step.isConditional(ctx)) {
      return i;
    }
    i = direction === 'forward' ? i + 1 : i - 1;
  }
  return direction === 'forward' ? tourSteps.length : -1;
}

export const tourSteps: TourStepConfig[] = [
  {
    target: '#dashboard-header-step',
    contentKey: 'tour.dashboard.welcome',
    path: '/',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: (ctx) => ctx.isDesktop ? '#header-theme-mode-toggle' : '#user-nav-avatar-btn',
    contentKey: 'tour.dashboard.theme_switch',
    path: '/',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#novel-selection-grid-step',
    contentKey: 'tour.dashboard.create_novel_step',
    path: '/',
    placement: 'right',
    isConditional: (ctx) => !ctx.selectedNovelId,
  },
  {
    target: '#novel-selection-grid-step',
    contentKey: 'tour.dashboard.create_novel_required',
    path: '/',
    placement: 'right',
    isConditional: (ctx) => !ctx.selectedNovelId,
  },
  {
    target: '#active-novel-selector-step',
    contentKey: 'tour.dashboard.novel_selector_step',
    path: '/',
    placement: 'bottom',
    isConditional: (ctx) => !!ctx.selectedNovelId,
  },
  {
    target: '#novel-identity-step',
    contentKey: 'tour.dashboard.identity_step',
    path: '/',
    placement: 'top',
    isConditional: (ctx) => !!ctx.selectedNovelId,
  },
  {
    target: '#sidebar-nav-step',
    contentKey: 'tour.dashboard.sidebar',
    path: '/',
    placement: 'right',
    isConditional: (ctx) => !!ctx.selectedNovelId,
    onNext: async (navigate) => {
      navigate({ to: '/characters' });
    }
  },
  {
    target: '#new-character-step',
    contentKey: 'tour.characters.create_btn',
    path: '/characters',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: 'a[href="/locations"]',
    contentKey: 'tour.sidebar.geography',
    path: '/characters',
    placement: 'right',
    waitForCondition: (ctx) => ctx.characterCount >= 2,
    onNext: async (navigate) => {
      navigate({ to: '/locations' });
    }
  },
  {
    target: '#new-location-step',
    contentKey: 'tour.locations.create_btn',
    path: '/locations',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: 'a[href="/scenes"]',
    contentKey: 'tour.sidebar.scenes',
    path: '/locations',
    placement: 'right',
    waitForCondition: (ctx) => ctx.locationCount >= 1,
    onNext: async (navigate) => {
      navigate({ to: '/scenes' });
    }
  },
  {
    target: '#new-chapter-step',
    contentKey: 'tour.scenes.new_chapter_btn',
    path: '/scenes',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '#add-scene-step',
    contentKey: 'tour.scenes.add_scene_btn',
    path: '/scenes',
    placement: 'top',
    isConditional: (ctx) => ctx.hasChapters,
    onNext: async (navigate, ctx) => {
      const chapterId = ctx.firstChapterId || '';
      navigate({ to: '/scenes/new', search: { chapterId } });
    }
  },
  {
    target: '#scene-characters-selector-step',
    contentKey: 'tour.scenes.link_entities',
    path: '/scenes/new',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '.onboarding-scene-card',
    contentKey: 'tour.scenes.go_to_editor',
    path: '/scenes',
    placement: 'top',
    waitForCondition: (ctx) => ctx.sceneCount >= 1,
    onNext: async (navigate, ctx) => {
      const textId = ctx.firstSceneTextId || '';
      navigate({ to: '/editor', search: { textId } });
    }
  },
  {
    target: '#editor-main-area-step',
    contentKey: 'tour.editor.main_area',
    path: '/editor',
    placement: 'center',
    disableBeacon: true,
    waitForCondition: (ctx) => !!ctx.hasTextId,
    onBefore: async () => {
      ensureDrawerState('#editor-outline-btn', false);
      ensureDrawerState('#editor-properties-btn', false);
      ensureDrawerState('#editor-entities-btn', false);
    }
  },
  {
    target: '#editor-outline-btn',
    contentKey: 'tour.editor.outline_btn',
    path: '/editor',
    placement: 'bottom',
    waitForCondition: (ctx) => !!ctx.hasTextId,
    onBefore: async () => {
      ensureDrawerState('#editor-outline-btn', true);
      ensureDrawerState('#editor-properties-btn', false);
      ensureDrawerState('#editor-entities-btn', false);
    }
  },
  {
    target: '#editor-properties-btn',
    contentKey: 'tour.editor.properties_btn',
    path: '/editor',
    placement: 'bottom',
    waitForCondition: (ctx) => !!ctx.hasTextId,
    onBefore: async () => {
      ensureDrawerState('#editor-outline-btn', false);
      ensureDrawerState('#editor-properties-btn', true);
      ensureDrawerState('#editor-entities-btn', false);
    }
  },
  {
    target: '#editor-entities-btn',
    contentKey: 'tour.editor.entities_btn',
    path: '/editor',
    placement: 'bottom',
    waitForCondition: (ctx) => !!ctx.hasTextId,
    onBefore: async () => {
      ensureDrawerState('#editor-outline-btn', false);
      ensureDrawerState('#editor-properties-btn', false);
      ensureDrawerState('#editor-entities-btn', true);
    },
    onNext: async (navigate) => {
      ensureDrawerState('#editor-outline-btn', false);
      ensureDrawerState('#editor-properties-btn', false);
      ensureDrawerState('#editor-entities-btn', false);
      navigate({ to: '/profile' });
    }
  },
  {
    target: '#edit-profile-name-btn',
    contentKey: 'tour.profile.edit_name',
    path: '/profile',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '#edit-profile-email-btn',
    contentKey: 'tour.profile.edit_email',
    path: '/profile',
    placement: 'right',
  },
  {
    target: '#user-nav-avatar-btn',
    contentKey: 'tour.profile.celebration',
    path: '/profile',
    placement: 'bottom',
  }
];
